<template>
    <div id="ritual-list-datatable">
        <DataTable :value="processedRituals" paginator :rows="50" :totalRecords="processedRituals.length"
            responsiveLayout="scroll"
            :globalFilterFields="['libelle', 'entrepriseNom', 'dateFormatted', 'canceledLabel', 'delayedLabel', 'pays']"
            v-model:filters="filters" filterDisplay="row" v-model:sort-field="sortField" v-model:sortOrder="sortOrder"
            @row-click="onRowClick" :customSort="true" @sort="onCustomSort" :loading="loading">
            <template #header>
                <div class="flex gap-2">
                    <IconField>
                        <InputIcon>
                            <i class="pi pi-search" />
                        </InputIcon>
                        <InputText v-model="filters['global'].value" :placeholder="$t('rituals.ritual_list.search')" />
                    </IconField>
                    <Button type="button" icon="pi pi-filter-slash" :label="$t('rituals.ritual_list.reset-filter')"
                        class="ml-auto button-default" @click="clearFilters" />
                </div>
            </template>
            <template #empty> {{ $t('rituals.ritual_list.no_data') }} </template>
            <template #loading> {{ $t('rituals.ritual_list.loading_data') }} </template>
            <Column style="width:150px;" field="pays" :header="$t('rituals.ritual_list.columns.2')" :sortable="true"
                :showFilterMenu="false">
                <template #body="slotProps">
                    <img :src="getEntrepriseDrapeau(slotProps.data.idEntreprise)" class="drapeau-img m-1 ml-0" />
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="availableCountries"
                        optionLabel="name" optionValue="name"
                        :placeholder="$t('rituals.ritual_list.filter-placeholder')" class="p-column-filter"
                        :maxSelectedLabels="1" :selectedItemsLabel="'{0} ' + $t('rituals.ritual_list.items_selected')">
                        <template #option="slotProps">
                            <div class="flex align-items-center">
                                <img :src="slotProps.option.flag" class="drapeau-img mr-2"
                                    style="width: 24px; height: auto;" v-if="slotProps.option.flag" />
                            </div>
                        </template>
                    </MultiSelect>
                </template>
            </Column>
            <Column style="width:350px;" field="idEntreprise" :header="$t('rituals.ritual_list.columns.3')"
                :sortable="true" :showFilterMenu="false">
                <template #body="slotProps">
                    {{ getEntrepriseLabel(slotProps.data.idEntreprise) }}
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <MultiSelect v-model="filterModel.value" @change="filterCallback()" filter
                        :options="availableEntreprises" optionLabel="nom" optionValue="id"
                        :placeholder="$t('rituals.ritual_list.filter-placeholder')" class="p-column-filter"
                        :maxSelectedLabels="1" :selectedItemsLabel="'{0} ' + $t('rituals.ritual_list.items_selected')">
                    </MultiSelect>
                </template>
            </Column>
            <Column style="width:175px;" field="date" filterField="dateSearch" :header="$t('rituals.ritual_list.columns.4')" :sortable="true"
                :showFilterMenu="false">
                <template #body="slotProps">
                    {{ slotProps.data.month.toString().padStart(2, '0') }} / {{ slotProps.data.year }}
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText v-model="filterModel.value" type="text" @input="filterCallback()" class="p-column-filter"
                        :placeholder="$t('rituals.ritual_list.filter-placeholder')" />
                </template>
            </Column>
            <Column field="libelle" :header="$t('rituals.ritual_list.columns.1')" :sortable="true"
                :showFilterMenu="false">
                <template #body="slotProps">
                    {{ slotProps.data.libelle }}
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText v-model="filterModel.value" type="text" @input="filterCallback()" class="p-column-filter"
                        :placeholder="$t('rituals.ritual_list.filter-placeholder')" />
                </template>
            </Column>
            <Column style="width:100px" field="canceledBySafetyLeader" :header="$t('rituals.ritual_list.columns.5')"
                :sortable="true" :showFilterMenu="false">
                <template #body="slotProps">
                    <Tag :value="$t('confirm_modal.' + (slotProps.data.canceledBySafetyLeader ? 'yes_button' : 'no_button'))"
                        :severity="slotProps.data.canceledBySafetyLeader ? 'danger' : 'success'" />
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Dropdown v-model="filterModel.value" @change="filterCallback()"
                        :options="[{ name: $t('confirm_modal.yes_button'), value: true }, { name: $t('confirm_modal.no_button'), value: false }]"
                        :placeholder="$t('rituals.ritual_list.filter-placeholder')" class="p-column-filter"
                        optionLabel="name" optionValue="value">
                        <template #option="slotProps">
                            <Tag :value="$t('confirm_modal.' + (slotProps.option.value ? 'yes_button' : 'no_button'))"
                                :severity="slotProps.option.value ? 'danger' : 'success'" />
                        </template>
                    </Dropdown>
                </template>
            </Column>
            <Column style="width:100px" field="delayed" :header="$t('rituals.ritual_list.columns.6')" :sortable="true"
                :showFilterMenu="false">
                <template #body="slotProps">
                    <Tag :value="$t('confirm_modal.' + (slotProps.data.delayed ? 'yes_button' : 'no_button'))"
                        :severity="slotProps.data.delayed ? 'danger' : 'success'" />
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Dropdown v-model="filterModel.value" @change="filterCallback()"
                        :options="[{ name: $t('confirm_modal.yes_button'), value: true }, { name: $t('confirm_modal.no_button'), value: false }]"
                        :placeholder="$t('rituals.ritual_list.filter-placeholder')" class="p-column-filter"
                        optionLabel="name" optionValue="value">
                        <template #option="slotProps">
                            <Tag :value="$t('confirm_modal.' + (slotProps.option.value ? 'yes_button' : 'no_button'))"
                                :severity="slotProps.option.value ? 'danger' : 'success'" />
                        </template>
                    </Dropdown>
                </template>
            </Column>
            <Column style="width: 1%; white-space: nowrap">
                <template #body="slotProps">
                    <Button class="p-button-primary mr-3" icon="pi pi-pencil" iconPos="right"
                        @click="editRitual(slotProps.data)" />
                    <Button class="p-button-danger" icon="pi pi-trash" iconPos="right"
                        @click="deleteRitual(slotProps.data.id)" />
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script>
import AsTitle from "../Elements/AsTitle.vue";
import DataTable from "primevue/datatable";
import Chip from "primevue/chip";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Tag from 'primevue/tag';
import { ref, computed } from "vue";
import { FilterMatchMode } from "primevue/api";
import { useI18n } from "vue-i18n";

export default {
    name: "RitualList",
    components: {
        AsTitle,
        DataTable,
        Chip,
        Column,
        Button,
        InputText,
        Dropdown,
        MultiSelect,
        IconField,
        InputIcon,
        Tag
    },
    props: {
        rituals: Array,
        entreprises: Array,
    },
    emits: ["onRowClick", "editRitual", "onDelete"],
    setup(props) {
        const { t } = useI18n();

        const initFilters = () => {
            return {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                libelle: { value: null, matchMode: FilterMatchMode.CONTAINS },
                idEntreprise: { value: null, matchMode: FilterMatchMode.IN },
                pays: { value: null, matchMode: FilterMatchMode.IN },
                dateSearch: { value: null, matchMode: FilterMatchMode.CONTAINS },
                canceledBySafetyLeader: { value: null, matchMode: FilterMatchMode.EQUALS },
                delayed: { value: null, matchMode: FilterMatchMode.EQUALS }
            };
        };

        const filters = ref(initFilters());

        const clearFilters = () => {
            filters.value = initFilters();
        };

        const loading = ref(false);

        const processedRituals = computed(() => {
            return (props.rituals || []).map(r => {
                const entreprise = (props.entreprises || []).find(e => e.id === r.idEntreprise);
                const mm = r.month.toString().padStart(2, '0');
                const dateFormatted = `${mm} / ${r.year}`;
                const dateSearch = `${mm} / ${r.year} ${mm}/${r.year}`;

                return {
                    ...r,
                    date: new Date(r.year, r.month - 1),
                    entrepriseNom: entreprise ? entreprise.nom : '',
                    pays: entreprise ? entreprise.pays : '',
                    dateFormatted: dateFormatted,
                    dateSearch: dateSearch,
                    canceledLabel: r.canceledBySafetyLeader ? t('confirm_modal.yes_button') : t('confirm_modal.no_button'),
                    delayedLabel: r.delayed ? t('confirm_modal.yes_button') : t('confirm_modal.no_button'),
                };
            });
        });

        const availableEntreprises = computed(() => {
            const usedIds = new Set((props.rituals || []).map(r => r.idEntreprise));
            return (props.entreprises || []).filter(e => usedIds.has(e.id));
        });

        const availableCountries = computed(() => {
            const countryMap = new Map();
            availableEntreprises.value.forEach(e => {
                const flagUrl = e.fichierDrapeau ? e.fichierDrapeau.url : null;
                if (e.pays && !countryMap.has(e.pays)) {
                    countryMap.set(e.pays, {
                        name: e.pays,
                        flag: flagUrl
                    });
                }
            });
            return Array.from(countryMap.values()).sort((a, b) => a.name.localeCompare(b.name));
        });

        return {
            filters,
            loading,
            processedRituals,
            availableEntreprises,
            availableCountries,
            clearFilters
        };
    },
    data() {
        return {
            sortField: 'year',
            sortOrder: -1,
        };
    },
    methods: {
        getEntrepriseLabel(id) {
            const entreprise = this.entreprises.find(e => e.id === id)
            return entreprise ? entreprise.nom : ''
        },
        getEntrepriseDrapeau(id) {
            const entreprise = this.entreprises.find(e => e.id === id)
            return entreprise ? entreprise.fichierDrapeau.url : ''
        },
        onRowClick(ritual) {
            this.$emit("onRowClick", ritual.data);
        },
        editRitual(ritual) {
            this.$emit("editRitual", ritual);
        },
        deleteRitual(ritualId) {
            this.$emit("onDelete", ritualId);
        },
        onCustomSort(event) {
            const field = event.sortField;
            const order = event.sortOrder;

            switch (field) {
                case 'date':
                case 'year':
                    this.processedRituals.sort((a, b) => {
                        return order * (a.date - b.date);
                    });
                    break;
                case 'idEntreprise':
                    this.processedRituals.sort((a, b) => {
                        const aEnterprise = this.getEntrepriseLabel(a.idEntreprise);
                        const bEnterprise = this.getEntrepriseLabel(b.idEntreprise);
                        return order * (aEnterprise.localeCompare(bEnterprise));
                    });
                    break;
                default:
                    this.processedRituals.sort((a, b) => {
                        let result = 0;

                        if (a[field] < b[field]) result = -1;
                        else if (a[field] > b[field]) result = 1;

                        return result * order;
                    });
                    break;
            }
        }

    },
};
</script>
<style lang="scss">
#ritual-list-datatable {
    .drapeau-img {
        height: 30px;
        display: flex;
    }

    .p-datatable-wrapper {
        .p-datatable-tbody tr {
            cursor: zoom-in;
            transition: background-color 0.2s ease-in-out;

            &:hover {
                background-color: #f5f5f5;
            }
        }
    }
}
</style>